import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  signInWithCustomToken,
} from 'firebase/auth';
import { auth, getEarlyAuthState } from '../config/firebase';
import {
  apolloClient,
  getAnonymousRunToken,
  setAnonymousRunToken,
} from '../config/apollo';
import { CLAIM_ANONYMOUS_RUN } from '../graphql/mutations';

// Participant claims from Firebase custom token
interface ParticipantClaims {
  participantId: string;
  planId: string;
  role: string;
  personId?: string;  // Global person ID (if using global participants)
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  // Participant-specific fields
  isParticipant: boolean;
  participantClaims: ParticipantClaims | null;
  signInWithMagicLink: (customToken: string) => Promise<void>;
  // Global person ID (for private subtasks and cross-plan features)
  personId: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Use early auth state if already resolved (listener started at module load
  // in firebase.ts, before React mounts). This eliminates the delay caused by
  // importing all page components before onAuthStateChanged fires.
  const earlyAuth = getEarlyAuthState();
  const [user, setUser] = useState<User | null>(earlyAuth.resolved ? earlyAuth.user : null);
  const [loading, setLoading] = useState(!earlyAuth.resolved);
  const [isParticipant, setIsParticipant] = useState(false);
  const [participantClaims, setParticipantClaims] = useState<ParticipantClaims | null>(null);
  const [personId, setPersonId] = useState<string | null>(null);

  useEffect(() => {
    console.log(`[AuthProvider] useEffect mount at ${performance.now().toFixed(0)}ms, earlyAuth resolved=${earlyAuth.resolved}`);
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log(`[AuthProvider] onAuthStateChanged at ${performance.now().toFixed(0)}ms, user=${user?.email || 'null'}`);
      setUser(user);

      // Check for participant claims in the token
      if (user) {
        try {
          const tokenResult = await user.getIdTokenResult();
          const claims = tokenResult.claims;

          // Extract personId from claims (for private subtasks and cross-plan features)
          if (claims.personId) {
            setPersonId(claims.personId as string);
          } else {
            setPersonId(null);
          }

          if (claims.participantId && claims.planId) {
            setIsParticipant(true);
            setParticipantClaims({
              participantId: claims.participantId as string,
              planId: claims.planId as string,
              role: (claims.role as string) || 'participant',
              personId: claims.personId as string | undefined,
            });
          } else {
            setIsParticipant(false);
            setParticipantClaims(null);
          }

          // S10 — claim/merge an anonymous-run session into the
          // brand-new UserPrincipal as soon as we observe a Firebase
          // sign-in event. We DO NOT block UI on this: the claim is
          // idempotent server-side (re-claim by same user transfers
          // 0 connections), and any active RUN connection that
          // wasn't transferred remains usable via its previous
          // anonymous owner until the next page reload. We only
          // clear the local anonymous token after the server
          // confirms the claim — partial failures keep the magic-
          // link session alive so the consumer doesn't lose access.
          const anonymousToken = getAnonymousRunToken();
          if (anonymousToken) {
            try {
              await apolloClient.mutate({
                mutation: CLAIM_ANONYMOUS_RUN,
                variables: { anonymousToken },
              });
              setAnonymousRunToken(null);
              try {
                await apolloClient.refetchQueries({ include: 'active' });
              } catch (refetchErr) {
                console.warn(
                  '[AuthProvider] post-claim refetch failed (non-fatal):',
                  refetchErr,
                );
              }
            } catch (claimErr) {
              console.error(
                '[AuthProvider] anonymous-run claim failed:',
                claimErr,
              );
              // Leave the anon token in place — the user can retry
              // by reloading the page; if the failure was an
              // already-claimed-by-different-user (FORBIDDEN), the
              // anonymous session simply continues to point at the
              // anonymous principal as a read-only fallback.
            }
          }
        } catch (error) {
          console.error('Error getting token claims:', error);
          setIsParticipant(false);
          setParticipantClaims(null);
          setPersonId(null);
        }
      } else {
        setIsParticipant(false);
        setParticipantClaims(null);
        setPersonId(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setIsParticipant(false);
      setParticipantClaims(null);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const signInWithMagicLink = async (customToken: string) => {
    try {
      await signInWithCustomToken(auth, customToken);
      // The onAuthStateChanged listener will update the state
    } catch (error) {
      console.error('Error signing in with magic link:', error);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    signOut,
    isParticipant,
    participantClaims,
    signInWithMagicLink,
    personId,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
